// @ts-nocheck
import React, { FC, useState } from 'react';
import {
  showBillRedirectState,
  userState,
  viewState,
  alertState
} from '../../atoms';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { sendEmailVerification } from "firebase/auth";
import { whichAuth as getAuth } from '../../getAuth';
import {
  checkmark,
  cloudUploadOutline,
  copyOutline,
  mailOutline,
} from 'ionicons/icons';
import { viewProviders } from '../../constants/global';
import { Notification } from '../../components'
import {
  IonHeader,
  IonTitle,
  IonText,
  IonButton,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon,
  IonRow,
  IonSpinner,
  IonItem,
  IonCheckbox,
  IonRadioGroup,
  IonRadio,
  IonLabel,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardSubtitle,
  useIonToast
} from '@ionic/react';
import { PageProps } from "../Page.model";
import { NotificationVariant } from "../../components/Notification/Notification.model";
import cn from "classnames";
import './Dashboard.css'
import './SignUpFlow.css'
import CheckIcon from '../../assets/Notification/check.svg'

const SignUpFlow: FC<PageProps> = () => {
  const user = useRecoilValue(userState);
  const setUser = useSetRecoilState(userState);
  const setView = useSetRecoilState(viewState);
  const setShowBillRedirect = useSetRecoilState(showBillRedirectState);
  const setAlert = useSetRecoilState(alertState);

  const [sendingVerificationEmail, setSendingVerificationEmail] = useState<boolean>(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState<boolean>(false);
  const [processing, setProcessing] = useState<string>(null);

  const [present] = useIonToast();
  
  const updateUser = newValue => getAuth().currentUser.getIdToken().then(token => {
    setProcessing(Object.keys(newValue)[0]);
    fetch(user._links.self.href, {
      method: 'PATCH',
      body: JSON.stringify(newValue),
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/merge-patch+json'
      }
    })
    .then(response => {
      if(response.status === 429) {
        setAlert({
          isOpen:true,
          title:'Smoov Payment Waitlist',
          subTitle:'We have reached our maximum capacity of users for smoov payments. We have added you to the waitlist and will let you know as soon as we can onboard more users for smoov payments.'
        });
        setUser({...user, ...{'paymentOption':user.paymentOption}});
      } else if(response.status === 200) {
        setUser({...user, ...newValue});
      }
    })
    .catch(() => setAlert({
      isOpen:true,
      title:'Failed to update preferences',
      subTitle:'There was an error saving your preferences. Please try again or contact us.'
    }))
    .finally(() => setProcessing(null))  
  });
  
  const resendVerificationEmail = async () => {
    setSendingVerificationEmail(true);
    sendEmailVerification(getAuth().currentUser)
      .then(() => setVerificationEmailSent(true))
      .catch(error => {
        if(error.code === "auth/too-many-requests") {
          setVerificationEmailSent(true);
        } else {
          setModalMessage('Failed to resend email: ' + error.code);
        }
      })
      .finally(() => setSendingVerificationEmail(false));
  }
  
  const redirectChoices = () => {
    if('CONTACTING_PROVIDERS' === user.status) {
      return (
        <Notification className="service-option"
          icon={checkmark}
          key="contactingProviders"
          variant={NotificationVariant.SUCCESS}
          title={
            <>
              Please contact your providers to update your billing email
              to <a
                href="#"
                className="underline"
                onClick={event => {navigator.clipboard.writeText(user.billingEmail); present('email address copied', 3000); event.preventDefault();}}
              >
                <small>{user.billingEmail} </small>
                <IonIcon icon={copyOutline}/>
              </a>
            </>
          }
          subTitle={
            <>
              Alternatively <a href="#" className="underline" onClick={event => {setView(viewProviders);event.preventDefault();}}> select your providers</a> and sign the authorisation. Then we'll do the hard work for you!
            </>
          }
        />
      )
    } else if('MANUAL_INVOICE_SEND' === user.status) {
      return (
        <Notification className="service-option"
          icon={checkmark}
          key="manualInvoiceSend"
          variant={NotificationVariant.SUCCESS}
          title={
            <>
              Please send your bills
              to <a
              href="#"
              className="underline"
              onClick={event => {navigator.clipboard.writeText(user.billingEmail); present('email address copied', 3000); event.preventDefault();}}
            >
              <small>{user.billingEmail} </small>
              <IonIcon icon={copyOutline}/>
            </a>
            </>
          }
          subTitle={
            <>
              Alternatively <a href="#" className="underline" onClick={event => {setView(viewProviders);event.preventDefault();}}>select your providers</a> and sign the authorisation to permanently redirect your bills.
            </>
          }
        />
      )
    } else if(!!user?.providers?.length && !user?.providers.every(provider => provider?.status === 'NEW')) {
      return (
        <Notification className="service-option"
          icon={checkmark}
          key="providersAdded"
          variant={NotificationVariant.SUCCESS}
          title="Providers added"
        />
      )
    } else {
      return (
        <Notification className="service-option"
          icon={cloudUploadOutline}
          title="Redirect your bills"
          key="authorisation"
          subTitle="So we can manage your bills, you need to redirect your bills to Billsmoov. It's easy..."
        >
          <IonButton
            className="item-dashboard-button"
            onClick={() => setShowBillRedirect(true)}
          >
            Redirect bills
          </IonButton>
        </Notification>
      )
    }
  }
  
  const option = (selected, title, subTitle) =>
    <IonCard className={cn('option-card', { 'selected': selected })}>
      <IonCardHeader>
        <IonCardTitle>
          {title}
        </IonCardTitle>
        <IonCardSubtitle className="no-border option-subtitle">
          {subTitle}
        </IonCardSubtitle>
      </IonCardHeader>
    </IonCard>
  
  return (
    <IonContent>
      <IonHeader className="payment-info-header">
        <IonTitle className="payment-info-header-subtitle">Welcome to Billsmoov</IonTitle>
        <IonText className="payment-info-header-started">
          { user?.status === 'NEW' ? 'Select your services' : 'Get started in 2 easy steps' }
        </IonText>
      </IonHeader>
      { user?.status === 'NEW' ?
        <IonGrid>
          <IonRow className="dashboard-row">
            <IonCol className="dashboard-row-col option-col" size="12">
              { option(user?.billComparisonEnabled,
                  <IonItem lines="none" onClick={() => updateUser({'billComparisonEnabled':!user.billComparisonEnabled})}>
                    {'billComparisonEnabled' === processing ? <IonSpinner name="dots" className="service-option-spinner" /> : <IonCheckbox slot="start" className="option-checkbox" checked={user.billComparisonEnabled} /> }
                    <IonLabel>Bill comparison</IonLabel>
                  </IonItem>,
                  <IonText>Billsmoov will automatically compare my bills to find better offers for me</IonText> )}
            </IonCol>
          </IonRow>
          
          <IonRow className="service-option-row">
            <IonCol>
              <IonLabel className="service-option-label">Payment options</IonLabel>
              <IonGrid>
                <IonRadioGroup value="paymentOption">
                      { option(user?.paymentOption === 'MANUAL',
                        <IonItem lines="none" className="service-option-item" onClick={() => updateUser({'paymentOption':'MANUAL'})}>
                          {'paymentOption' === processing ? <IonSpinner name="dots" className="service-option-spinner" /> : <IonRadio slot="start" className="option-checkbox" checked={user.paymentOption === 'MANUAL'} /> }
                          <IonLabel>Manual Payment</IonLabel>
                        </IonItem>,
                        <IonText>I want to keep paying bills myself.</IonText>) }
                      { option(user?.paymentOption === 'AUTO',
                        <IonItem lines="none" className="service-option-item" onClick={() => updateUser({'paymentOption':'AUTO'})}>
                          {'paymentOption' === processing ? <IonSpinner name="dots" className="service-option-spinner" /> : <IonRadio slot="start" className="option-checkbox" checked={user.paymentOption === 'AUTO'} /> }
                          <IonLabel>Automatic Payment</IonLabel>
                        </IonItem>,
                        <IonText>Billsmoov will pay my bills from my account.</IonText>) }
                      { option(user?.paymentOption === 'SMOOV',
                        <IonItem lines="none" className="service-option-item" onClick={() => updateUser({'paymentOption':'SMOOV'})}>
                          {'paymentOption' === processing ? <IonSpinner name="dots" className="service-option-spinner" /> : <IonRadio slot="start" className="option-checkbox" checked={user.paymentOption === 'SMOOV'} /> }
                          <IonLabel>Smoov Payment</IonLabel>
                        </IonItem>,
                        <IonText>Billsmoov will pay my bills for me and charge me a smooth weekly payment.</IonText>) } 
                </IonRadioGroup>
              </IonGrid>
            </IonCol>
          </IonRow>
          <IonRow className="service-option-row">
            <IonCol>
              <IonButton onClick={() => updateUser({'status':'CONFIGURATION_COMPLETE'})}
                disabled={user.paymentOption === 'NONE'}
                className="item-dashboard-button">
                { 'status' === processing && <IonSpinner name="dots" /> } Next
              </IonButton>
            </IonCol>
          </IonRow>
        </IonGrid>
        
        :
        
        <IonGrid>
          <IonRow className="dashboard-row">
            <IonCol className="dashboard-row-col" size="6">
              {user.emailVerified ?
                <Notification className="service-option"
                  icon={CheckIcon}
                  key="email"
                  title="Email confirmed"
                  variant={NotificationVariant.SUCCESS}
                  subTitle="Thank you for confirming your email address"
                />
                :
                <Notification className="service-option"
                  icon={mailOutline}
                  key="email"
                  title="Verify email address"
                  subTitle={`Please verify your email address by clicking the link in the email we sent to ${user.email}`}
                >
                  {verificationEmailSent ?
                    <IonButton disabled={true} className="item-dashboard-button check">
                      <IonIcon icon={checkmark} /> Please check your inbox or junk mail
                    </IonButton> :
                    <IonButton onClick={resendVerificationEmail} className="item-dashboard-button">
                      {sendingVerificationEmail && <IonSpinner name="dots" />}
                      Resend email
                    </IonButton>}
                </Notification>
              }
            </IonCol>
            <IonCol className="dashboard-row-col" size="6">
              { redirectChoices() }
            </IonCol>
          </IonRow>
        </IonGrid>
      }
    </IonContent>
);}

export default SignUpFlow;
